class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

(() => {
  const TTL_MS = 24 * 60 * 60 * 1000;
  const STORAGE_KEY = 'loubna_cart_expiry_v1';
  let running = false;

  let storage;
  try {
    storage = window.localStorage;
    const testKey = '__loubna_cart_expiry_test__';
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
  } catch (error) {
    return;
  }

  if (typeof window.fetch !== 'function') return;

  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart.js`;
  const clearUrl = `${root}cart/clear.js`;

  const resetState = () => storage.removeItem(STORAGE_KEY);

  const getSignature = (cart) =>
    JSON.stringify(
      (cart.items || [])
        .map((item) => [item.key, item.quantity])
        .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
    );

  const saveState = (signature, updatedAt = Date.now()) => {
    storage.setItem(STORAGE_KEY, JSON.stringify({ signature, updatedAt }));
  };

  const readState = () => {
    try {
      return JSON.parse(storage.getItem(STORAGE_KEY) || 'null');
    } catch (error) {
      resetState();
      return null;
    }
  };

  const clearExpiredCart = async () => {
    const response = await fetch(clearUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({}),
    });

    if (!response.ok) throw new Error('Unable to clear expired cart');

    resetState();
    window.location.reload();
  };

  const enforceCartExpiry = async () => {
    if (running) return;
    running = true;

    try {
      const response = await fetch(cartUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) return;

      const cart = await response.json();

      if (!cart || cart.item_count === 0) {
        resetState();
        return;
      }

      const now = Date.now();
      const currentSignature = getSignature(cart);
      const state = readState();

      if (
        !state ||
        state.signature !== currentSignature ||
        !Number.isFinite(Number(state.updatedAt)) ||
        Number(state.updatedAt) <= 0 ||
        Number(state.updatedAt) > now
      ) {
        saveState(currentSignature, now);
        return;
      }

      if (now - Number(state.updatedAt) >= TTL_MS) {
        await clearExpiredCart();
      }
    } catch (error) {
      // Never block normal storefront behavior if the expiry check fails.
    } finally {
      running = false;
    }
  };

  enforceCartExpiry();

  window.addEventListener('pageshow', enforceCartExpiry);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') enforceCartExpiry();
  });

  try {
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, enforceCartExpiry);
    }
  } catch (error) {
    // Pub/sub integration is optional; page-load checks are the fallback.
  }
})();
